'use client';

import './project-item.scss';
import Link from 'next/link';
import { ProjectModel } from '@/lib/models/Project';
import { RouterName } from '@/lib/enum/RouterEnum';
import { useSwiperSlide } from 'swiper/react';
import { useEffect, useRef, useState } from 'react';
import { SentenceScramble } from '@/lib/utils/SentenceScramble';
import { useSwipeUpNavigation } from '@/lib/hooks/useSwipeUpNavigation';

interface Props {
  project: ProjectModel;
  index: number;
  goToDetail: () => void;
  realIndex: number | null;
}

export default function ProjectMobileItem (props: Props) {
  const { project, index, goToDetail } = props;
  const { handleTouchStart, handleTouchEnd } = useSwipeUpNavigation({ onSwipeUp: goToDetail });
  const swiperSlide = useSwiperSlide();
  const shouldLoadVideo = swiperSlide.isActive || swiperSlide.isPrev || swiperSlide.isNext;
  const hasScrambledOnce = useRef(false); // persists across renders
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (swiperSlide.isActive && !hasScrambledOnce.current) {
      const sentenceScrambler = new SentenceScramble(`project-${index}-scrambler`, () => {}, true);
      sentenceScrambler.startScramble(0.66, 0);
      // sentenceScrambler.startSequentialScramble(0.25);
      hasScrambledOnce.current = true;
    }
  }, [swiperSlide?.isActive]);

  // Re-trigger playback when this slide becomes active. The `autoPlay`
  // attribute only fires on initial source load, so a video mounted while
  // off-screen (prev/next) never starts once centered. Calling play() here
  // also recovers from autoplay being deferred under CPU/network throttling.
  useEffect(() => {
    const video = videoRef.current;
    if (swiperSlide.isActive && video) {
      video.play().catch(() => {});
    }
    // Allow a retry next time the slide comes back into range.
    if (!shouldLoadVideo && hasError) {
      setHasError(false);
    }
  }, [swiperSlide.isActive, shouldLoadVideo, hasError]);

  return (
    <div
      className={'project-list-home'}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="project-slide-top">
        <dl className='project-description-list'>
          <dt className='text-title-mobile'>CLIENT</dt>
          <dd className={`text-title-desc-mobile project-${index}-scrambler`}>{project.clientName}</dd>
          <dt className='text-detail-mobile'>EXPERIMENT</dt>
          <dd className={`text-detail-desc-mobile`}><h2 className={`project-${index}-scrambler`}>{project.mobileProjectTitle || project.projectTitle}</h2></dd>
        </dl>
      </div>
      
      <div className="projects-slide-bottom">
        <Link
          className='project-mobile-container'
          href={`${RouterName.projectDetail}/${project.slug}`}
          aria-label={`View ${project.projectTitle} project details`}
          prefetch={false}
          onNavigate={(e) => {
            e.preventDefault();
            goToDetail();
          }}
        >
          <div className='image-project-mobile'>
            {hasError ? (
              <div
                className="thumbnail-video-fallback"
                role="img"
                aria-label={`${project.projectTitle} thumbnail unavailable`}
              />
            ) : (
              <video
                ref={videoRef}
                autoPlay={shouldLoadVideo}
                loop 
                muted
                playsInline
                src={shouldLoadVideo ? project.mobileThumbnailVideo : undefined}
                preload={shouldLoadVideo ? 'auto' : 'none'}
                aria-hidden="true"
                onError={() => { if (shouldLoadVideo) setHasError(true); }}
              >
                <track
                  kind="captions"
                  src="/captions/no-audio.vtt"
                  srcLang="en"
                  label="No spoken audio"
                />
              </video>
            )}
          </div>
        </Link>
      </div>
    </div>
  )
};
